/**
 * Response Ideas gallery — ready-made Yes/No/Maybe sets for the form builder.
 *
 * Fills the three existing Response Options inputs (#yesOptionText,
 * #noOptionText, #maybeOptionText). It owns no data of its own: the sets live
 * in the per-language builder-content file as `responsePresets`, and applying
 * one is exactly equivalent to the host typing into those inputs.
 *
 * Why per-language data rather than one English list translated at click time:
 * these labels ride the translation API when a host publishes a multilingual
 * form, and a preset that reads like a person wrote it in English becomes
 * literal nonsense in Hebrew. Each language gets its own culturally-at-home
 * sets. A builder whose language file has no `responsePresets` simply doesn't
 * show the button — no empty modal, no half-translated list.
 *
 * Sets a preset by dispatching a real `input` event, which is what the
 * dedicated listener in builder-core.js (~line 1723) watches to sync
 * builderOptions and refresh the preview. Do not also poke builderOptions or
 * call updatePreview here — that listener already does both.
 */
(function () {
    'use strict';

    // Guest-facing labels wrap onto a second line past roughly this length in a
    // WhatsApp webview, which is the narrowest surface a form is opened in.
    const MAX_LABEL_LENGTH = 35;

    // Guest-facing order: the question, then the answers.
    const INPUT_IDS = ['attendingLabel', 'yesOptionText', 'noOptionText', 'maybeOptionText'];

    // Only the three answer fields get a length counter — the question sits on
    // its own line in the guest form and has more room than a button label.
    const COUNTED_INPUT_IDS = ['yesOptionText', 'noOptionText', 'maybeOptionText'];

    const DEFAULT_ACCENT = '#E8B4A8';

    function t(key, fallback) {
        const map = window.getBuilderTranslations ? window.getBuilderTranslations() : {};
        return (map && map[key]) || fallback;
    }

    function escapeHtml(value) {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function getPresetData() {
        const content = window.BuilderLanguageSystem
            && typeof window.BuilderLanguageSystem.getLanguageContent === 'function'
            ? window.BuilderLanguageSystem.getLanguageContent()
            : null;
        const data = content && content.responsePresets;
        if (!data || !Array.isArray(data.presets) || data.presets.length === 0) {
            return null;
        }
        return data;
    }

    /* ---------------------------------------------------------------- accent */

    function hexToRgba(hex, alpha) {
        const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim());
        if (!match) return null;
        const [r, g, b] = match.slice(1).map(part => parseInt(part, 16));
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    /**
     * Re-publish the builder's Primary Color onto the modal so the mock option
     * buttons match the form actually being built. Read fresh on every open —
     * the host may have changed the colour since last time.
     */
    function syncAccent(modal) {
        const picker = document.getElementById('primaryColor');
        const fromOptions = window.builderCore
            && window.builderCore.builderOptions
            && window.builderCore.builderOptions.color;
        const accent = (picker && picker.value) || fromOptions || DEFAULT_ACCENT;
        const soft = hexToRgba(accent, 0.12);
        if (!soft) return; // unparseable colour: keep the stylesheet's default
        modal.style.setProperty('--preset-accent', accent);
        modal.style.setProperty('--preset-accent-soft', soft);
    }

    /* ---------------------------------------------------------------- render */

    function renderTabs(data, activeCategory) {
        const list = document.getElementById('responsePresetTabs');
        if (!list) return;

        const categories = [{ id: 'all', label: t('labels.preset_category_all', 'All') }]
            .concat(Array.isArray(data.categories) ? data.categories : []);

        list.innerHTML = categories.map(category => `
            <li class="nav-item" role="presentation">
                <button class="nav-link rounded-pill px-3 py-1 small${category.id === activeCategory ? ' active' : ''}"
                        type="button" role="tab" data-preset-category="${escapeHtml(category.id)}"
                        aria-selected="${category.id === activeCategory ? 'true' : 'false'}">
                    ${escapeHtml(category.label)}
                </button>
            </li>
        `).join('');
    }

    function renderOptionRow(value, variant) {
        if (!value) return '';
        return `
            <div class="preset-option preset-option--${variant}">
                <span class="preset-dot" aria-hidden="true"></span>
                <span>${escapeHtml(value)}</span>
            </div>
        `;
    }

    function renderCards(data, category) {
        const container = document.getElementById('responsePresetContainer');
        if (!container) return;

        const presets = category === 'all'
            ? data.presets
            : data.presets.filter(preset => preset.category === category);

        container.innerHTML = presets.map(preset => `
            <div class="col-12 col-sm-6 col-lg-4">
                <button type="button" class="preset-card" data-preset-id="${escapeHtml(preset.id)}">
                    <span class="preset-name">${escapeHtml(preset.name)}</span>
                    <span class="preset-question">${escapeHtml(preset.question || data.defaultQuestion || '')}</span>
                    <div class="preset-options">
                        ${renderOptionRow(preset.yes, 'yes')}
                        ${renderOptionRow(preset.no, 'no')}
                        ${renderOptionRow(preset.maybe, 'maybe')}
                    </div>
                    <span class="preset-use">
                        <i class="bi bi-check-lg" aria-hidden="true"></i>${escapeHtml(t('buttons.use_this_set', 'Use this set'))}
                    </span>
                </button>
            </div>
        `).join('');
    }

    /* ----------------------------------------------------------------- apply */

    function applyPreset(preset, data) {
        const values = {
            // A set is only coherent if the question and the answers arrive
            // together, so the attendance question is ALWAYS written — a preset
            // that defines none falls back to the language's default rather than
            // inheriting the previously applied preset's question.
            //
            // It must never be written as an empty string: getInputValue returns
            // '' for a cleared field and the fallback in builder-translations.js
            // only fires on null/undefined, so a blank here reaches the guest
            // form as a blank question.
            attendingLabel: preset.question || data.defaultQuestion || null,
            yesOptionText: preset.yes,
            noOptionText: preset.no,
            maybeOptionText: preset.maybe
        };

        INPUT_IDS.forEach(id => {
            const value = values[id];
            const input = document.getElementById(id);
            if (!input || !value) return;
            input.value = value;
            // builder-core's response-option listener reads .value off these
            // events to update builderOptions and refresh the preview, so a
            // preset behaves identically to typing.
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // The Maybe label is filled even when the Maybe option is switched off,
        // so it is already in place if the host turns it on later. The toggle is
        // deliberately NOT flipped: whether to offer Maybe at all is a headcount
        // decision the host has already made.

        if (window.showToast) {
            window.showToast(t('messages.preset_applied', 'Response labels updated'), 'success');
        }
    }

    /* ------------------------------------------------------- character count */

    /**
     * Adds a live "12 / 35" counter beside each label input's hint text.
     *
     * The counter is a SIBLING of the hint, never a child: the hint carries a
     * data-translate attribute and the translation applier overwrites the
     * element's textContent, which would silently eat a nested counter.
     */
    function attachCounter(inputId) {
        const input = document.getElementById(inputId);
        if (!input || input.dataset.presetCounter) return;

        // The hint sits immediately after the input's .form-floating wrapper.
        const floating = input.closest('.form-floating') || input;
        const hint = floating.nextElementSibling;
        if (!hint || !hint.classList.contains('form-text')) return;

        const row = document.createElement('div');
        row.className = 'preset-hint-row ' + (hint.className.includes('mb-3') ? 'mb-3' : '');
        hint.classList.remove('mb-3');
        hint.parentNode.insertBefore(row, hint);
        row.appendChild(hint);

        const counter = document.createElement('small');
        // Deliberately NOT .form-text: that class carries an `!important` grey
        // in the vendor stylesheet, which silently beats the over-limit amber
        // no matter how specific .preset-count.is-over gets.
        counter.className = 'preset-count small';
        counter.setAttribute('aria-hidden', 'true');
        counter.title = t('descriptions.response_length_hint',
            'Keep labels under 35 characters so they stay on one line on phones.');
        row.appendChild(counter);

        const update = () => {
            // Count what the guest actually sees: an empty input renders its
            // placeholder as the live label.
            const shown = input.value || input.placeholder || '';
            const length = Array.from(shown).length; // emoji are >1 UTF-16 unit
            counter.textContent = `${length} / ${MAX_LABEL_LENGTH}`;
            counter.classList.toggle('is-over', length > MAX_LABEL_LENGTH);
        };

        input.addEventListener('input', update);
        input.dataset.presetCounter = '1';
        update();
    }

    /* ------------------------------------------------------------------ wire */

    const ResponsePresets = {
        activeCategory: 'all',

        open() {
            const modal = document.getElementById('responsePresetsModal');
            const data = getPresetData();
            if (!modal) return;
            if (!data) {
                if (window.showToast) {
                    window.showToast(
                        t('messages.presets_unavailable', "Ready-made sets aren't available in this language yet."),
                        'info'
                    );
                }
                return;
            }

            syncAccent(modal);
            this.activeCategory = 'all';
            renderTabs(data, this.activeCategory);
            renderCards(data, this.activeCategory);
            new bootstrap.Modal(modal).show();
        },

        init() {
            const trigger = document.getElementById('openResponsePresetsBtn');
            const modal = document.getElementById('responsePresetsModal');
            if (!trigger || !modal) return;

            // No sets written for this language yet — hide the entry point
            // rather than offering an empty or half-English gallery.
            if (!getPresetData()) {
                trigger.style.display = 'none';
                return;
            }
            trigger.style.display = '';

            trigger.addEventListener('click', event => {
                event.preventDefault();
                this.open();
            });

            // Delegated so the handlers survive every re-render.
            modal.addEventListener('click', event => {
                const tab = event.target.closest('[data-preset-category]');
                if (tab) {
                    const data = getPresetData();
                    if (!data) return;
                    this.activeCategory = tab.getAttribute('data-preset-category');
                    renderTabs(data, this.activeCategory);
                    renderCards(data, this.activeCategory);
                    return;
                }

                const card = event.target.closest('[data-preset-id]');
                if (!card) return;
                const data = getPresetData();
                if (!data) return;
                const preset = data.presets.find(item => item.id === card.getAttribute('data-preset-id'));
                if (!preset) return;

                applyPreset(preset, data);

                // Hold the applied state briefly so the click registers visually
                // before the modal dismisses.
                card.classList.add('is-applied');
                setTimeout(() => {
                    const instance = bootstrap.Modal.getInstance(modal);
                    if (instance) instance.hide();
                }, 180);
            });

            COUNTED_INPUT_IDS.forEach(attachCounter);
        }
    };

    window.ResponsePresets = ResponsePresets;

    function boot() {
        // The preset list arrives with the language content file, which may load
        // after this script.
        if (window.BuilderLanguageSystem && typeof window.BuilderLanguageSystem.waitForLanguageContent === 'function') {
            window.BuilderLanguageSystem.waitForLanguageContent().then(() => ResponsePresets.init());
        } else {
            ResponsePresets.init();
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
